import { Editor, Path, Element as SlateElement, Transforms, } from 'slate';
/**
 * Does the file have a mime type indicating it's an image.
 *
 * @param file File instance to check
 * @returns Boolean
 */
function isImageFile(file) {
    return file.type.indexOf('image/') === 0;
}
/** Returns a unique Uid to identify the file */
function createFileUid(file, index) {
    return `${file.name + new Date().getTime()}-${index}`;
}
/** From a file object return a local src and unique uid to indetify the file */
function createImage(file, index) {
    const uid = createFileUid(file, index);
    const URLObj = window.URL || window.webkitURL;
    const src = URLObj.createObjectURL(file);
    return {
        src,
        uid,
    };
}
/**
 * Updates an image element in the document by looking it up using the file uid
 * Replaces the src property with `newSrc`
 *
 * @param editor Slate.js editor instance
 * @param uid File Uid
 * @param newSrc Src to update the image to point to
 */
function updateImage(editor, uid, newSrc) {
    Transforms.setNodes(editor, {
        src: newSrc,
    }, {
        at: [],
        match: (node) => SlateElement.isElement(node) &&
            node.type === 'image' &&
            node.uid === uid,
    });
}
/**
 * Function to remove an image element from the document using the image file
 * uid
 */
function removeImageOnError(editor, uid) {
    Transforms.removeNodes(editor, {
        at: [],
        match: (node) => SlateElement.isElement(node) && node.type === 'image' && node.uid === uid,
    });
}
/**
 * Slate.js editor plugin that add support of image elements and adds functions
 * to insert and upload images
 */
export const withImages = (fileUpload, alertError) => (editor) => {
    const { isVoid, insertBreak } = editor;
    editor.isVoid = (element) => {
        return element.type === 'image' ? true : isVoid(element);
    };
    editor.insertBreak = (...args) => {
        if (!editor.selection) {
            insertBreak(...args);
            return;
        }
        const [parentNode, parentPath] = Editor.parent(editor, editor.selection.focus.path);
        if (SlateElement.isElement(parentNode) && editor.isVoid(parentNode)) {
            const nextPath = Path.next(parentPath);
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, {
                at: nextPath,
                select: true,
            });
        }
        else {
            insertBreak(...args);
        }
    };
    editor.insertImage = (url, uid) => {
        const text = { text: '' };
        const image = {
            type: 'image',
            src: url,
            uid,
            children: [text],
        };
        Transforms.insertNodes(editor, image);
    };
    editor.uploadImages = (files) => {
        if (files === null || !files)
            return;
        const filesArray = Array.from(files);
        filesArray.forEach((file, index) => {
            if (isImageFile(file)) {
                const { src, uid } = createImage(file, index);
                editor.insertImage(src, uid);
                fileUpload(file)
                    .then(({ link }) => updateImage(editor, uid, link))
                    .catch((e) => {
                    removeImageOnError(editor, uid);
                    alertError('Failed to upload image, please try again.');
                });
            }
        });
    };
    return editor;
};
